import { useState } from 'react';
import { Search } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  Input,
  PageHeader,
  Skeleton,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { statusTone } from '@/lib/status';
import {
  PAYMENT_PROVIDER_LABEL,
  PAYMENT_PURPOSE_LABEL,
  PAYMENT_STATUS_LABEL,
  usePayments,
} from '@/lib/orders-api';
import { errorMessage, formatCurrency, formatDateTime } from '@/lib/utils';

/**
 * Payment records across every order (spec §23).
 *
 * Read-only, and deliberately so: money is recorded against the order it
 * belongs to, on the order screen, where the outstanding balance is in view and
 * paying it in full confirms the order in the same transaction. A general
 * "add a payment" form here would let someone record a receipt against nothing.
 */
export function PaymentsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const payments = usePayments({ page, limit: 20, search });

  return (
    <div>
      <PageHeader
        title="Payments"
        description="Every payment recorded against an order or a security deposit."
      />

      <Card className="mb-4 p-4">
        <div className="relative min-w-56">
          <Search
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search payment number"
            className="pl-9"
            aria-label="Search payments"
          />
        </div>
      </Card>

      <Card>
        {payments.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : payments.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load payments">{errorMessage(payments.error)}</ErrorAlert>
          </div>
        ) : (payments.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="No payments yet"
            description="Payments recorded against orders and deposits will appear here."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Payment</Th>
                <Th>Purpose</Th>
                <Th>Provider</Th>
                <Th>Status</Th>
                <Th className="text-right">Amount</Th>
                <Th>Reference</Th>
              </tr>
            </thead>
            <tbody>
              {payments.data?.items.map((payment) => (
                <tr key={payment.id} className="hover:bg-canvas/60">
                  <Td>
                    <p className="font-medium text-ink">{payment.paymentNumber}</p>
                    <p className="text-xs text-ink-subtle">
                      {formatDateTime(payment.paidAt ?? payment.createdAt)}
                    </p>
                  </Td>
                  <Td className="text-sm text-ink-muted">
                    {PAYMENT_PURPOSE_LABEL[payment.purpose] ?? payment.purpose}
                  </Td>
                  <Td className="text-sm text-ink-muted">
                    {PAYMENT_PROVIDER_LABEL[payment.provider] ?? payment.provider}
                    {payment.method && (
                      <span className="block text-xs text-ink-subtle">{payment.method}</span>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={statusTone(payment.status)}>
                      {PAYMENT_STATUS_LABEL[payment.status]}
                    </Badge>
                  </Td>
                  <Td className="text-right font-medium tabular-nums">
                    {formatCurrency(payment.amount)}
                    {payment.refundedAmount > 0 && (
                      <span className="block text-xs text-ink-subtle">
                        {formatCurrency(payment.refundedAmount)} refunded
                      </span>
                    )}
                  </Td>
                  <Td className="text-sm text-ink-muted">{payment.reference ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {payments.data && payments.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {payments.data.pagination.total} payments · page {payments.data.pagination.page} of{' '}
              {payments.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!payments.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!payments.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
